import { CloseCircleOutlined, MailOutlined, PlusOutlined } from '@ant-design/icons'
import { Button } from 'antd'
import clsx from 'clsx'
import { FormikContext, useFormik } from 'formik'
import { FC, forwardRef, useImperativeHandle } from 'react'
import { useTranslation } from 'react-i18next'

import { EmployeesInvitationForm } from './employees-invitation-interface'
import styles from './employees-section.module.scss'
import { EmployeesInvitationValidationSchema } from './validators/employees-invitation-validation-schema'
import { PureInput } from '../../../../../common/ui/pure-input/pure-input'
import { LocalStorageService } from '../../../../../utils/local-storage-service/local-storage-service'
import { StepperKey } from '../../constants/auth.constants'

type InvitationSectionProps = {
  initialValue?: EmployeesInvitationForm
}

export const InvitationSection: FC<InvitationSectionProps> = forwardRef(({ initialValue }: InvitationSectionProps, ref) => {
  const { t } = useTranslation()

  const userData = LocalStorageService.get('userData') || {}
  const companyConfig = userData[StepperKey.CompanyConfig] || {}
  const employeesConfigJson = companyConfig.employees || '{"min":"0","max":"3"}' // Default to 3 if not found

  let employeesLimit = 3 // Default fallback
  const parsedEmployees = JSON.parse(employeesConfigJson)
  employeesLimit = parseInt(parsedEmployees.max) || 3

  const getInitialValues = (): EmployeesInvitationForm => {
    if (initialValue?.emails && initialValue.emails.length > 0) {
      return initialValue
    }

    return { emails: ['', ''] }
  }

  const formik = useFormik<EmployeesInvitationForm>({
    initialValues: getInitialValues(),
    validationSchema: EmployeesInvitationValidationSchema(),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: () => {},
  })

  useImperativeHandle(ref, () => ({
    getValues: () => {
      return {
        errors: formik.validateForm(),
        values: formik.values,
      }
    },
  }))

  const addEmail = () => {
    formik.setFieldValue('emails', [...formik.values.emails, ''])
    console.log(formik.values.emails)
  }

  const handleDelete = (index: number) => {
    const filteredEmails = formik.values.emails.filter((_, idx) => idx !== index)
    formik.setFieldValue('emails', filteredEmails)
  }

  return (
    <FormikContext.Provider value={formik as any}>
      <h2 className="mt-3 mb-5">{t('onboarding.employeesInvitation.title')}</h2>
      <div
        className={clsx(
          styles.employeesConfig,
          'step-wrapper row d-flex align-items-center bg-white rounded-4 pb-5 pt-4 px-1 px-md-3 px-lg-5 mb-5 gx-5',
        )}
      >
        {formik.values.emails.map((_, index) => (
          <div
            key={index}
            className={clsx(styles.employeesConfigInputContainer, 'col-12 col-md-6 col-lg-4 px-1 px-md-3')}
          >
            <PureInput
              {...formik.getFieldProps(`emails[${index}]`)}
              error={formik.errors.emails?.[index]}
              icon={<MailOutlined />}
              label={t('onboarding.employeesInvitation.emailLabel')}
              moduleStyle="mb-3 mt-1"
              required={false}
              size="middle"
            />
            <CloseCircleOutlined
              className={clsx(styles.employeesConfigDeleteButton)}
              onClick={() => handleDelete(index)}
            />
          </div>
        ))}
        <div className="col-12 col-md-6 col-lg-4 mb-3 px-1 px-md-3">
          <p className="fs-6 mb-1 mt-3">{t('onboarding.employeesInvitation.emailLabel')}</p>
          <Button
            className={clsx(styles.employeesConfigButtonAdd)}
            disabled={formik.values.emails.length >= employeesLimit}
            icon={<PlusOutlined />}
            iconPosition="end"
            onClick={addEmail}
            size="middle"
            block
          >
            {t('onboarding.employeesInvitation.addButton')}
          </Button>
        </div>

        <div className="px-1 px-md-3">
          <p className="mt-3 fs-7 text-secondary">
            {t('onboarding.employeesInvitation.slotsRemaining')}
            {employeesLimit - formik.values.emails.length}
          </p>
        </div>
      </div>
    </FormikContext.Provider>
  )
})
